import Box from '@mui/material/Box';
import WebNavButton from '../NavigationButton/WebNavButton';
import {
  Storage,
  Schedule,
  SmartToy,
  Settings,
  Leaderboard,
  ViewColumn,
  PersonSearch
} from '@mui/icons-material';
import { useTargetPersonaSearch } from '../../context/TargetPersonaSearchContext';
import {
  useCampaignContextController,
  useSpreadsheetViewType
} from '../../context/CampaignContext';
import { CampaignProspectType } from '../../api/campaigns';

export enum ToolOption {
  COLUMN_EDITOR = 'Column',
  AI_ASSISTANT = 'AI Assistant',
  SCHEDULING = 'Scheduling',
  CRM_SYNC = 'CRM Sync',
  TARGET_PERSONA = 'Target Persona',
  PROSPECT_PRIORITIZATION = 'Prioritize Prospects',
  SETTINGS = 'Campaign Settings'
}

interface WebNavSidebarProps {
  isManager: boolean;
  isAdmin: boolean;
  isBeta: boolean;
  selectedTab: ToolOption | null;
  onTabSelect: (tab: ToolOption | null) => void;
  /** When Column button is clicked: toggle close if open, else open for selection. */
  onColumnButtonClick?: () => void;
}

const WebNavSidebar = (props: WebNavSidebarProps) => {
  const { isSearchRunning } = useTargetPersonaSearch();
  const [campaignState] = useCampaignContextController();
  const viewType = useSpreadsheetViewType();
  const currentCampaign = campaignState.currentCampaign;
  const isAccountCampaign =
    currentCampaign?.prospect_type === CampaignProspectType.ACCOUNT;
  const isLeadView = viewType === 'lead';
  const hideOnLeadView = isAccountCampaign && isLeadView;
  const showTargetPersona = isAccountCampaign && !isLeadView;
  const showScheduling = !hideOnLeadView;
  const showAIAssistant = !hideOnLeadView;

  const clickSelectedTab = (tab: ToolOption) => {
    if (props.selectedTab === tab) {
      props.onTabSelect(null);
    } else {
      props.onTabSelect(tab);
    }
  };

  return (
    <Box
      sx={(theme) => ({
        width: '100%',
        height: '100%',
        padding: '10px 2px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'start',
        boxSizing: 'content-box',
        gap: 1,
        backgroundColor: 'transparent'
      })}
    >
      <WebNavButton
        icon={ViewColumn}
        onClick={() =>
          props.onColumnButtonClick
            ? props.onColumnButtonClick()
            : clickSelectedTab(ToolOption.COLUMN_EDITOR)
        }
        text={ToolOption.COLUMN_EDITOR}
        isSelected={props.selectedTab === ToolOption.COLUMN_EDITOR}
        disabled={false}
      />
      {showAIAssistant && (
        <WebNavButton
          icon={SmartToy}
          onClick={() => clickSelectedTab(ToolOption.AI_ASSISTANT)}
          text={ToolOption.AI_ASSISTANT}
          isSelected={props.selectedTab === ToolOption.AI_ASSISTANT}
          disabled={false}
        />
      )}
      {showScheduling && (
        <WebNavButton
          icon={Schedule}
          onClick={() => clickSelectedTab(ToolOption.SCHEDULING)}
          text={ToolOption.SCHEDULING}
          isSelected={props.selectedTab === ToolOption.SCHEDULING}
          disabled={false}
        />
      )}
      <WebNavButton
        icon={Storage}
        onClick={() => clickSelectedTab(ToolOption.CRM_SYNC)}
        text={ToolOption.CRM_SYNC}
        isSelected={props.selectedTab === ToolOption.CRM_SYNC}
        disabled={false}
      />
      <WebNavButton
        icon={Leaderboard}
        onClick={() => clickSelectedTab(ToolOption.PROSPECT_PRIORITIZATION)}
        text={ToolOption.PROSPECT_PRIORITIZATION}
        isSelected={props.selectedTab === ToolOption.PROSPECT_PRIORITIZATION}
        disabled={false}
      />
      {showTargetPersona && (
        <WebNavButton
          icon={PersonSearch}
          onClick={() => clickSelectedTab(ToolOption.TARGET_PERSONA)}
          text={ToolOption.TARGET_PERSONA}
          isSelected={props.selectedTab === ToolOption.TARGET_PERSONA}
          disabled={false}
          showRunningIndicator={isSearchRunning}
        />
      )}
      <WebNavButton
        icon={Settings}
        onClick={() => clickSelectedTab(ToolOption.SETTINGS)}
        text={ToolOption.SETTINGS}
        isSelected={props.selectedTab === ToolOption.SETTINGS}
        disabled={false}
      />
    </Box>
  );
};

export default WebNavSidebar;
